import { getCurrencyFormat, getDecimalFormat, getPercentFormat } from '@/i18n/numbers'
import { setColumnsVistas } from '@/services/grids/setColumnsVistas'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import store from '@/store'
import { i18n } from '@/i18n'
import { passNumber, passMinus, passMayus, passSpecial } from '@/utils/regex'

const feedbackMixin = {
  data () {
    return {
      previouslyFocusedEl: null,
    }
  },
  methods: {
    // Generación de mensajes pop up en pantalla
    doFeedback: function (message = null, type = 'error', duration = 3000, isAccessibleOnly = false) {
      if (!message) {
        message = this.$t('feedback.error.general')
      }
      if (store.getters['app/alertsTimeExtended']) duration = 30000
      
      if (isAccessibleOnly) {
        const region = document.getElementById('sr-only-live-region')
        if (region) {
          region.textContent = ''
          setTimeout(() => { region.textContent = message }, 50)
        } 
        console.log(`[Alerta accesible] ${type}: ${message}`)
        return
      }
      
      // Mensaje visual normal
      const options = {
        message,
        type,
        duration,
        showClose: true,
      }
      this.$message(options)
      this.$nextTick(() => {
        const alerts = document.querySelectorAll('.el-message[role="alert"]')
        alerts.forEach(alert => {
          alert.setAttribute('aria-live', 'assertive')
          const closeBtn = alert.querySelector('.el-message__closeBtn')
          if (closeBtn && !closeBtn.dataset.wcagPatched) {
            closeBtn.dataset.wcagPatched = '1'
            closeBtn.setAttribute('tabindex', '0')
            closeBtn.setAttribute('role', 'button')
            closeBtn.setAttribute('aria-label', 'Cerrar')
            closeBtn.addEventListener('keydown', (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                closeBtn.click()
              }
            })
          }
        })
      })
    },
    resetControlFormChanges () {
      setTimeout(() => {
        store.commit('app/setFormChanges', false)
      }, 100)
    },
    preSelectClient (target, field) {
      if (this.empresaFilter) {
        const idCliente = this.clientes.filter(x => x.Nombre === this.empresaFilter)[0]?.IdEmpresa || null
        if (idCliente) {
          target[field] = idCliente
          this.doFeedback(this.$i18n.t('configurations.preselected_client_filter', { client: this.empresaFilter }),
            'info', 3000, true)
        }
      }
    },
    // Guarda la ordenación y la agrupación en la store de persistencia equivalente
    updatePersistedSortMixin () {
      this.$store.commit(this.vuexnamespace + 'Persisted/setSort', this.kendo.sort)
      this.$store.commit(this.vuexnamespace + 'Persisted/setGroup', this.kendo.group)
    },
    loadGridPersistedMixin () {
      this.$store.dispatch('notifications/resetStateWithoutSort')
      this.$store.dispatch('historicalUser/resetStateWithoutSort')
      this.$store.dispatch('historicalGroup/resetStateWithoutSort')
      this.$store.dispatch('historicalRule/resetStateWithoutSort')
      this.$store.dispatch('historicalConfigflow/resetStateWithoutSort')
      this.$store.dispatch('historicalProceding/resetStateWithoutSort')
      this.$store.dispatch('historicalOrgprovinces/resetStateWithoutSort')
      this.$store.dispatch('historicalAPIrequests/resetStateWithoutSort')
      this.$store.dispatch('historicalClient/resetStateWithoutSort')
      this.$store.dispatch('historicalCert/resetStateWithoutSort')
      this.$store.dispatch('historicalCompany/resetStateWithoutSort')
      this.$store.dispatch('historicalNEO/resetStateWithoutSort')
      this.$store.dispatch('companies/resetStateWithoutSort')
      this.$store.dispatch('locations/resetStateWithoutSort')
      this.$store.dispatch('companylocations/resetStateWithoutSort')
      this.$store.dispatch('companyothercertificates/resetStateWithoutSort')
      this.$store.dispatch('clientlocations/resetStateWithoutSort')
      this.$store.dispatch('certificates/resetStateWithoutSort')
      this.$store.dispatch('certcompanies/resetStateWithoutSort')
      this.$store.dispatch('clients/resetStateWithoutSort')
      this.$store.dispatch('users/resetStateWithoutSort')
      this.$store.dispatch('userscompanies/resetStateWithoutSort')
      this.$store.dispatch('userstatus/resetStateWithoutSort')
      this.$store.dispatch('groups/resetStateWithoutSort')
      this.$store.dispatch('groupusers/resetStateWithoutSort')
      this.$store.dispatch('groupcompanies/resetStateWithoutSort')
      this.$store.dispatch('groupstatus/resetStateWithoutSort')
      this.$store.dispatch('groupgroups/resetStateWithoutSort')
      this.$store.dispatch('neogroups/resetStateWithoutSort')
      this.$store.dispatch('neopresentations/resetStateWithoutSort')
      this.$store.dispatch('usergroups/resetStateWithoutSort')
      this.$store.dispatch('groupingcompanies/resetStateWithoutSort')
      this.$store.dispatch('groupinglocations/resetStateWithoutSort')
      this.$store.dispatch('groupingagencies/resetStateWithoutSort')
      this.$store.dispatch('rules/resetStateWithoutSort')
      this.$store.dispatch('rulegroups/resetStateWithoutSort')
      this.$store.dispatch('configflow/resetStateWithoutSort')
      this.$store.dispatch('orgprovinces/resetStateWithoutSort')
      this.$store.dispatch('apirequests/resetStateWithoutSort')
      this.$store.dispatch('procedings/resetStateWithoutSort')
      this.$store.dispatch('documentmaster/resetStateWithoutSort')
      this.$store.dispatch('imports/resetStateWithoutSort')
      this.$store.dispatch('distributors/resetStateWithoutSort')
      this.$store.dispatch('distributorlocations/resetStateWithoutSort')
      this.$store.dispatch('historicalDistributor/resetStateWithoutSort')
      this.$store.dispatch('trashUsers/resetStateWithoutSort')
      this.$store.dispatch('trashHistoricalUsers/resetStateWithoutSort')
      this.$store.dispatch('trashNotifications/resetStateWithoutSort')
      this.$store.dispatch('trashHistoricalNotifications/resetStateWithoutSort')
      this.$store.dispatch('trashCertificates/resetStateWithoutSort')
      this.$store.dispatch('trashHistoricalCertificates/resetStateWithoutSort')
      this.$store.dispatch('trashCompanies/resetStateWithoutSort')
      this.$store.dispatch('trashHistoricalCompanies/resetStateWithoutSort')
      this.$store.dispatch('trashGroups/resetStateWithoutSort')
      this.$store.dispatch('trashHistoricalGroups/resetStateWithoutSort')
      this.$store.dispatch('trashClients/resetStateWithoutSort')
      this.$store.dispatch('trashHistoricalClients/resetStateWithoutSort')
    },
    // Mapea todos los datos de kendo a seleccionado o no seleccionado y establece los seleccionados
    mapSelectionKendoGrouping (checked) {
      this.selected = []

      if (this.kendo.group && this.kendo.group.length > 0) {
        // Si detectamos agrupaciones:
        const countGroups = this.kendo.group.length
        const tempGridData = this.setKendoGroupingRecursive(this.kendo.dataResult.data, countGroups, checked)

        this.kendo.dataResult.data = tempGridData
      } else {
        // Si no hay agrupaciones
        const tempGridData = this.kendo.dataResult.data.map((item) => {
          return { ...item, selected: checked }
        })
        this.kendo.dataResult.data = tempGridData
      }

      if (checked) {
        this.selected = this.data.map(x => x)
      }
    },
    // Cambia de forma recursiva a un valor los campos seleccionados en base a profundidad de recursividad indicada como longitud
    setKendoGroupingRecursive (data, longitud, checked) {
      if (longitud === 0) {
        return data.map((item) => {
          return { ...item, selected: checked }
        })
      }

      return data.map((group) => {
        const items = this.setKendoGroupingRecursive(group.items, longitud - 1, checked)
        return { ...group, items }
      })
    },
    // Generación de objeto con columnas y agrupaciones para la exportación a Excel
    getColumnsGroupExcelMixin: function (kendoColumns = null, kendoGroups = null) {
      let res = []
      if (kendoColumns === null || kendoGroups === null) return res
      res = kendoColumns.filter(x => {
        if ((x.hidden === false || x.hidden === undefined) && x.field !== 'selected' && x.field !== ' ' && x.field !== undefined) return x
      }).sort((a, b) => {
        return a.orderIndex - b.orderIndex
      }).map(x => {
        return { columna: x.field, group: false, ordenGroup: -1 }
      })
      let ordenBase = 0
      kendoGroups.forEach(group => {
        res.map(x => {
          if (x.columna === group.field) {
            x.group = true
            x.ordenGroup = ordenBase
          }
          return x
        })
        ordenBase = ordenBase + 1
      })
      res.forEach(x => {
        if (x.columna.includes('.')) {
          x.columna = x.columna.substring(x.columna.indexOf('.') + 1, x.columna.length)
        }
      })
      return res
    },
    // Generación de objeto de ordenación y ordenación de agrupación para petición en back
    getObjOrderMixin: function (kendoGroup = null, kendoSort = null) {
      let obj = {}
      if (kendoGroup === null || kendoSort === null) return obj
      if (kendoGroup.length > 0) {
        kendoGroup.forEach(element => {
          let name = element.field.toLowerCase()
          if (name.includes('.')) {
            name = name.split('.')[0]
          }
          obj[name] = (element.dir === undefined || element.dir === 'asc')
        })
        obj = { order: obj }
      }
      const obj2 = {}
      if (kendoSort.length > 0) {
        kendoSort.forEach(element => {
          let name = element.field.toLowerCase()
          if (name.includes('.')) {
            name = name.split('.')[0]
          }
          obj2[name] = (element.dir === 'asc')
        })
        if (obj.order) {
          obj.order = { ...obj.order, ...obj2 }
        } else {
          obj = { order: obj2 }
        }
      }
      return obj
    },
    // Enviar datos de columnas por vista
    sendUpdatedColumns: async function (columns) {
      try {
        const sendDataColumns = columns.filter(x => x.hidden !== true).sort(function (a, b) {
          return a.orderIndex - b.orderIndex
        }).map(x => x.field).filter(x => {
          if (x !== undefined && x !== null && x !== '' && x !== 'selected') {
            return x
          }
        }).join(';')
        // console.log('sendDataColumns', sendDataColumns)
        
        const payload = {
          IdVista: this.idVista,
          Columnas: sendDataColumns,
          Version: this.$store.getters['app/version']
        }
        const response = await setColumnsVistas(payload)
        if (response.blnError === true) {
          console.err('Error: sendUpdatedColumns')
        } 
      } catch (error) {
        console.err(error)
        // this.doFeedback(error)
      } 
    },
    // Truncar textos
    truncate: function (str, n, useWordBoundary) {
      if (str.length <= n) { return str }
      const subString = str.substr(0, n - 1) // the original check
      return (useWordBoundary
        ? subString.substr(0, subString.lastIndexOf(' '))
        : subString) + '...'
    },
    // Obtiene la extensión de un nombre de archivo con un regex
    getExtensionFromFile (text) {
      const regex = /(?:\.([^.]+))?$/
      const res = regex.exec(text)[1]
      return res
    },
    // Descarga en el navegador un fichero
    downloadBlob (response, filename) {
      const blob = new Blob([response.data], { type: response.data.type, encoding: 'UTF-8' })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      filename = decodeURI(filename)
      link.download = filename
      link.click()
    },
    // Devuelve una promesa para obtener el base64 de un archivo
    getBase64 (file) {
      return new Promise(function (resolve, reject) {
        const reader = new FileReader()
        let res = ''
        reader.readAsDataURL(file)
        reader.onload = function () {
          res = reader.result
        }
        reader.onerror = function (error) {
          reject(error)
        }
        reader.onloadend = function () {
          resolve(res)
        }
      })
    },
    // Genera notificaciones de windows
    notifyWindows: function (title = 'Title', body = 'body', tag = null) {
      if (!window.Notification) {
        console.log('Browser does not support notifications.')
      } else {
        const idNotify = tag === null ? uuidv4() : tag
        const routeIcon = window.location.origin + '/ms-icon-310x310.png'
        // check if permission is already granted
        if (Notification.permission === 'granted') {
          // show notification here
          var notify = new Notification(title, {
            body,
            icon: routeIcon,
            tag: idNotify,
          })
          console.log('Send notify', notify)
        } else {
          // request permission from user
          Notification.requestPermission().then(function (p) {
            if (p === 'granted') {
              // show notification here
              var notify = new Notification(title, {
                body,
                icon: routeIcon,
                tag: idNotify,
              })
              console.log('Send notify', notify)
            } else {
              console.log('User blocked notifications.')
            }
          }).catch(function (err) {
            console.error(err)
          })
        }
      }
    },
    // Devuelve el valor de un tag (Tags de Filtros y Búsquedas)
    getValueTagFormated (tagObj) {
      // console.log(tagObj)
      
      if (Array.isArray(tagObj[2])) {
        if (tagObj[0] === 'estadoDescargaExtra') {
          const tmp = tagObj[2].map(y => this.tiposEstadoDescarga.filter(x => x.id === y)[0].Nombre)
          return tmp.join(', ')
        }
        if (tagObj[0] === 'moduloMultiple') {
          const tmp = tagObj[2].map(y => this.tiposModulos.filter(x => x.id === y)[0].name)
          return tmp.join(', ')
        }
        if (tagObj[0] === 'eventoMultiple') {
          const tmp = tagObj[2].map(y => y)
          return tmp.join(', ')
        }
        return tagObj[2].join(', ')
      } else if (tagObj[0] === 'metadato1' || tagObj[0] === 'metadato2' || tagObj[0] === 'metadato3' || tagObj[0] === 'metadato4' || tagObj[0] === 'metadato5') {
        const labelOperator = this.operators.filter(x => x.value === tagObj[2].operador)[0].nombreTag
        return '"' + tagObj[2].campo + '"' + labelOperator + '"' + tagObj[2].valor + '"'
      } else if (tagObj[0] === 'estadoDescargaExtra' || tagObj[0] === 'estadoOrigen' || tagObj[0] === 'estadoDestino') {
        const EstadoNombre = this.tiposEstadoDescarga.filter(x =>
          x.id === tagObj[2]
        )[0].Nombre
        return EstadoNombre
      } else if (tagObj[0] === 'role') {
        const rolName = this.roles.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return rolName
      } else if (tagObj[0] === 'tipoAuth') {
        const tipoAuthName = this.tiposAuth.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tipoAuthName
      } else if (tagObj[0] === 'idTipoHSM') {
        const tipoHSMName = this.tiposHSM.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tipoHSMName
      } else if (tagObj[0] === 'idTipoCliente') {
        const tipoClienteName = this.tiposCliente.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tipoClienteName
      } else if (tagObj[0] === 'tipoGrupo') {
        const tiposGrupo = this.tiposGrupo.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tiposGrupo
      } else if (tagObj[0] === 'tipoCert') {
        const tipoCert = this.tiposCert.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tipoCert
      } else if (tagObj[0] === 'tipoDistribuidor') {
        const tipoDistribuidor = this.tiposDistribuidor.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tipoDistribuidor
      } else if (tagObj[0] === 'tipoProvisionamiento') {
        const tipoProvisionamiento = this.tiposProvisionamiento.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tipoProvisionamiento
      } else if (tagObj[0] === 'estadoDiasCortesiaAEAT') {
        const estadoDiasCortesiaName = this.estadosDiasCortesia.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return estadoDiasCortesiaName
      } else if (tagObj[0] === 'tipoImportacion') {
        const tipoImportacion = this.tiposImportacion.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tipoImportacion
      } else if (tagObj[0] === 'MomentoAplicacion') {
        const tipoMomentoAplicacionRegla = this.tiposMomentoAplicacionReglas.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tipoMomentoAplicacionRegla
      } else if (tagObj[0] === 'idEstadoSugerencia') {
        const estadoSugerencia = this.estadosSugerencias.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return estadoSugerencia
      } else if (tagObj[0] === 'FaseConfiguracion') {
        const FaseConfiguracion = this.tiposFasesConfiguracion.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return FaseConfiguracion
      } else if (tagObj[0] === 'PaqueteContratacion') {
        const PaqueteContratacion = this.tiposPaquetesContratacion.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return PaqueteContratacion
      } else if (tagObj[0] === 'TiposAutenticacion') {
        const TiposAutenticacion = this.tiposAutenticacion.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return TiposAutenticacion
      } else if (tagObj[0] === 'tipoImportacionEstado') {
        const tipoImportacionEstado = this.tiposImportacionEstado.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tipoImportacionEstado 
      } else if (tagObj[0] === 'tipoDiligencia') {
        const tipoDiligencia = this.tiposDiligencias.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tipoDiligencia 
      } else if (tagObj[0] === 'tipoDiligenciaEstado') {
        const tipoDiligenciaEstado = this.tiposDiligenciasEstado.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tipoDiligenciaEstado 
      } else if (tagObj[0] === 'tipoPresentacionEstado') {
        const tipoPresentacionEstado = this.tiposPresentacionEstado.filter(x =>
          x.id === tagObj[2]
        )[0].name
        return tipoPresentacionEstado 
      } else if (tagObj[0] === 'evento') {
        return tagObj[2].replaceAll('_', ' ')
      } else if (tagObj[2] === true) {
        return 'sí'
      } else if (tagObj[2] === false) {
        return 'no'
      } else if (tagObj[1].includes('desde') || tagObj[1].includes('hasta')) {
        return moment(tagObj[2]).format('DD-MM-YYYY')
      } else if (tagObj[0] === 'partner' || tagObj[0] === 'tipoServicio' || tagObj[0] === 'grupoVigilante') {
        return tagObj[3]
      } else if (tagObj[0] === 'tipoNotif') {
        return this.$store.getters['app/tiposNotificacion'].filter(x => x.id === tagObj[2])[0].Nombre
      }
      return tagObj[2]
    },
    // Validación contraseñas element ui
    isPasswordValid: function (rule, value, callback, { validation = this.validation }) {
      if (!value) {
        validation.numChar = false
        validation.numMin = false
        validation.charMinus = false
        validation.charMayus = false
        validation.especial = false
        return this.doAriaLiveAndReturnCallback(i18n.t('validation.complete'), callback)
      } else {
        validation.numChar = !(value.length < 12)
        validation.numMin = passNumber.test(value)
        validation.charMinus = passMinus.test(value)
        validation.charMayus = passMayus.test(value)
        validation.especial = passSpecial.test(value)

        if (Object.values(validation).some(x => x !== true)) {
          return this.doAriaLiveAndReturnCallback(i18n.t('validation.pass.main'), callback)
        }
      }
      callback()
    },
    // Validación repetición de contraseña element ui
    isRepeatPasswordValid: function (rule, value, callback, { validationRepeat = this.validationRepeat, checkPassword = this.form.password }) {
      if (!value) {
        validationRepeat.numChar = false
        validationRepeat.numMin = false
        validationRepeat.charMinus = false
        validationRepeat.charMayus = false
        validationRepeat.especial = false
        validationRepeat.passRepeat = false
        return this.doAriaLiveAndReturnCallback(i18n.t('validation.complete'), callback)
      } else {
        validationRepeat.numChar = !(value.length < 12)
        validationRepeat.numMin = passNumber.test(value)
        validationRepeat.charMinus = passMinus.test(value)
        validationRepeat.charMayus = passMayus.test(value)
        validationRepeat.especial = passSpecial.test(value)
        validationRepeat.passRepeat = value === checkPassword

        if (Object.keys(validationRepeat).filter(x => x !== 'passRepeat').some(x => validationRepeat[x] !== true)) {
          return this.doAriaLiveAndReturnCallback(i18n.t('validation.pass.main'), callback)
        }
        if (!validationRepeat.passRepeat) {
          return this.doAriaLiveAndReturnCallback(i18n.t('validation.pass.pass_repeat'), callback)
        }
      }
      callback()
    },
    setLocale (locale, bus = null) {
      this.$store.commit('app/setSelectedLang', locale)
      this.$i18n.locale = locale
      this.$i18n.fallbackLocale = locale

      if (bus) bus.$emit('resetAndUpdateNav', 1000)
    },
    tiposAuthFilteredMixin (tiposAuthBase, role, tipoInstalacion) {
      const tiposAuthFinal = JSON.parse(JSON.stringify(tiposAuthBase))
      if (role !== 'SuperAdministrador') {
        if (tipoInstalacion === 'STANDARD') {
          tiposAuthFinal[1].disabled = true
          tiposAuthFinal[2].disabled = true
          tiposAuthFinal[3].disabled = true
          tiposAuthFinal[4].disabled = true
        }
        if (tipoInstalacion === 'AAD') {
          tiposAuthFinal[0].disabled = true
          tiposAuthFinal[2].disabled = true
          tiposAuthFinal[3].disabled = true
          tiposAuthFinal[4].disabled = true
        }
        if (tipoInstalacion === 'HYBRID') {
          tiposAuthFinal[3].disabled = true
          tiposAuthFinal[4].disabled = true
        }
        if (tipoInstalacion === 'SAML2') {
          tiposAuthFinal[0].disabled = true
          tiposAuthFinal[1].disabled = true
          tiposAuthFinal[2].disabled = true
          tiposAuthFinal[4].disabled = true
        }
        if (tipoInstalacion === 'HYBRIDSAML2') {
          tiposAuthFinal[1].disabled = true
          tiposAuthFinal[2].disabled = true
        }
      }
      return tiposAuthFinal
    },
    // Gestion de validación de errores accesible
    doAriaLiveAndReturnCallback (message, callback) {
      this.doFeedback(message, 'info', 3000, true)
      callback(new Error(message))
    },
    doAriaLiveAndReturnMessage (message) {
      console.log('message', message)
      
      this.doFeedback(message, 'info', 3000, true)
      return message
    },
    isGenericValidation: function (rule, value, callback) {
      // Permitimos valores 0 o false
      if (value === null || value === undefined || value === '') {
        return this.doAriaLiveAndReturnCallback(i18n.t('validation.complete'), callback)
      } else {
        callback()
      }
    },
    /// /// WORKAROUNDS / HACKS 
    // Detecta si se esta usando chrome
    detectChrome () {
      const userAgent = navigator.userAgent
      // Detecta Chrome excluyendo Edge y Opera que también incluyen "Chrome" en userAgent
      return /Chrome/.test(userAgent) && !/Edge|OPR/.test(userAgent)
    },
    // Mueve al focus al siguiente elemento dependiendo del botón del teclado pulsado (guardado en lastKey)
    focusElementByKey () {
      let nextElement = null
      if (this.lastKey === 'Tab') {
        nextElement = this.getFocusable('next')
      } else if (this.lastKey === 'Shift+Tab') {
        nextElement = this.getFocusable('prev')
      }
      if (nextElement) nextElement.focus()
    },
    // Obtiene el siguiente elemento focusable
    getFocusable (direction = 'next') {
      const focusableSelectors = [
        'a[href]',
        'area[href]',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'button:not([disabled])',
        'iframe',
        'object',
        'embed',
        '[contenteditable]',
        '[tabindex]:not([tabindex="-1"])'
      ]

      const focusableElements = Array.from(document.querySelectorAll(focusableSelectors.join(',')))
        .filter(el => el.offsetParent !== null) // visibles

      const current = document.activeElement
      const index = focusableElements.indexOf(current)

      if (direction === 'next' && index >= 0 && index < focusableElements.length - 1) {
        return focusableElements[index + 1]
      }

      if (direction === 'prev' && index > 0) {
        return focusableElements[index - 1]
      }

      return null
    },
    /// Element UI
    fixAccesibilityElInputPassword (elPasswordInput, msgAriaLabel) {
      if (!elPasswordInput || !elPasswordInput.$el) return

      const inputEl = elPasswordInput?.$el

      // Función para aplicar todo si no esta aplicado ya
      const applyAccessibility = () => {
        const pwdToggle = inputEl.querySelector('.el-icon-view, .el-icon-view-off')

        if (pwdToggle && !pwdToggle.hasAttribute('data-accessible')) {
          pwdToggle.setAttribute('aria-label', msgAriaLabel)
          pwdToggle.setAttribute('role', 'button')
          pwdToggle.setAttribute('tabindex', '0')
          pwdToggle.setAttribute('data-accessible', 'true')

          pwdToggle.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              pwdToggle.click()
            }
          })
        }
      }

      // Observar el DOM por cambios (íconos que aparecen al escribir)
      const observer = new MutationObserver(() => {
        applyAccessibility()
      })

      observer.observe(inputEl, {
        childList: true,
        subtree: true,
      })

      // También aplicar al escribir (cuando aparece el icono)
      const nativeInput = inputEl.querySelector('input')
      if (nativeInput) {
        nativeInput.addEventListener('input', () => {
          applyAccessibility()
        })
      }

      // Llamada inicial por si ya está el icono
      this.$nextTick(() => {
        applyAccessibility()
      })
    },
    // Captura la última tecla de desplacamiento si no es chrome
    captureKeyNotChrome (e) {
      if (this.detectChrome()) return
      if (e.key === 'Tab') {
        this.lastKey = e.shiftKey ? 'Shift+Tab' : 'Tab'
      } else {
        this.lastKey = null
      }
    },
    // Añade display block o none a la clase deseada
    // --- ACCESIBILIDAD EL-DATE-PICKER ---
    // Añade aria-live al popover del date-picker si no existe
    ensureAriaLiveDatePickerPopover (className) {
      const panel = document.querySelector(className)
      console.log('[ARIA-LIVE] ensureAriaLiveDatePickerPopover', className, panel)
      if (panel && !panel.querySelector('.aria-live-date')) {
        const liveDiv = document.createElement('div')
        liveDiv.className = 'aria-live-date'
        liveDiv.setAttribute('aria-live', 'polite')
        liveDiv.setAttribute('style', 'position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden;')
        panel.appendChild(liveDiv)
        console.log('[ARIA-LIVE] aria-live-date appended')
      }
    },

    // Actualiza el aria-live con la fecha sobre la que está el foco
    updateAriaLiveDatePickerPopover (className, dateStr) {
      const panel = document.querySelector(className)
      console.log('[ARIA-LIVE] updateAriaLiveDatePickerPopover', className, dateStr, panel)
      if (panel) {
        const liveDiv = panel.querySelector('.aria-live-date')
        const isVisible = panel.offsetParent !== null && panel.style.display !== 'none'
        console.log('[ARIA-LIVE] aria-live-date visible?', isVisible)
        if (liveDiv) {
          // Forzar el cambio para que el lector lo detecte
          liveDiv.textContent = ''
          setTimeout(() => {
            liveDiv.textContent = dateStr
            console.log('[ARIA-LIVE] Valor que se mete en aria-live:', dateStr)
            console.log('[ARIA-LIVE] aria-live-date updated:', dateStr)
            console.log('[ARIA-LIVE] aria-live-date actual:', liveDiv.textContent)
          }, 10)
        } else {
          console.warn('[ARIA-LIVE] aria-live-date not found')
        }
      } else {
        console.warn('[ARIA-LIVE] panel not found')
      }
    },

    // Observa el calendario y añade listeners a las celdas de fecha
    // Al recibir el foco en el date-picker hacemos visible su popover (si no es chrome) 
    onFocusDatePickerMixin (className) {
      // Llama a la mejora de accesibilidad SIEMPRE antes de comprobar el navegador
      setTimeout(() => { this.enhanceDatePickerAccessibility(className) }, 50)
      if (this.detectChrome()) return
      setTimeout(() => { this.toggleDisplayPopoverDatepicker(className, true) }, 100)
    },
    // Al recibir el blur del date-picker redirigimos el foco al siguiente elemento dependiendo de si se avanza o se retrocede
    // Además ocultamos el popover
    // (Si no es chrome)
    onBlurDatePickerMixin (className) {
      setTimeout(() => { this.enhanceDatePickerAccessibility(className) }, 50)
      if (this.detectChrome()) return
      setTimeout(() => { this.focusElementByKey() }, 100)
      setTimeout(() => { this.toggleDisplayPopoverDatepicker(className, false) }, 300)
    },
    enhanceDatePickerAccessibility (className) {
      this.ensureAriaLiveDatePickerPopover(className)
      const panel = document.querySelector(className)
      console.log('[ARIA-LIVE] enhanceDatePickerAccessibility', className, panel)
      if (!panel) {
        console.warn('[ARIA-LIVE] panel not found for enhanceDatePickerAccessibility')
        return
      }
      // --- NUEVO: MutationObserver para aria-live con td.available.current ---
      const updateAriaLiveFromCurrentCell = () => {
        const currentCell = panel.querySelector('.el-date-table td.available.current')
        let label = ''
        if (currentCell) {
          label = currentCell.getAttribute('aria-label') || currentCell.textContent
        }
        // Extraer mes y año del header del popover
        let month = ''
        let year = ''
        const header = panel.querySelector('.el-date-picker__header')
        if (header) {
          console.log('[ARIA-LIVE] header:', header)
          // Element UI suele tener el mes y año en .el-date-picker__header-label
          const headerLabel = header.querySelectorAll('.el-date-picker__header-label')
          if (headerLabel) {
            year = headerLabel[0].textContent.trim()
            month = headerLabel[1].textContent.trim()
          }
        }
        // Combina valor, mes y año
        let ariaLiveValue = label
        if (month && year) {
          ariaLiveValue += ' de ' + month + ' de ' + year
        }
        console.log('[ARIA-LIVE] aria-live value:', ariaLiveValue)
        this.updateAriaLiveDatePickerPopover(className, ariaLiveValue)
        hideDefaultPopoverContent()
      }

      // Oculta el contenido por defecto del popover a los lectores de pantalla
      // Solo deja visible el aria-live
      const hideDefaultPopoverContent = () => {
        // Oculta todas las celdas del calendario
        const tableCells = panel.querySelectorAll('.el-date-table td')
        tableCells.forEach(td => {
          td.setAttribute('aria-hidden', 'true')
          // Elimina tabindex si existe
          if (td.hasAttribute('tabindex')) {
            td.removeAttribute('tabindex')
            console.log('[ARIA-LIVE] hideDefaultPopoverContent: tabindex removed', td)
          }
          // Elimina roles interactivos
          if (td.hasAttribute('role')) {
            td.removeAttribute('role')
            console.log('[ARIA-LIVE] hideDefaultPopoverContent: role removed', td)
          }
          // NO modificar el texto ni el contenido visual
        })
        console.log('[ARIA-LIVE] hideDefaultPopoverContent: td aria-hidden applied', tableCells.length)
        // Oculta el header del calendario
        const header = panel.querySelector('.el-date-picker__header')
        if (header) {
          header.setAttribute('aria-hidden', 'true')
          // Elimina tabindex y roles
          if (header.hasAttribute('tabindex')) header.removeAttribute('tabindex')
          if (header.hasAttribute('role')) header.removeAttribute('role')
          // NO modificar el texto ni el contenido visual
          console.log('[ARIA-LIVE] hideDefaultPopoverContent: header aria-hidden applied')
        } else {
          console.log('[ARIA-LIVE] hideDefaultPopoverContent: header not found')
        }
        // Oculta otros elementos visibles excepto el aria-live
        const children = Array.from(panel.children)
        children.forEach(child => {
          if (!child.classList.contains('aria-live-date')) {
            child.setAttribute('aria-hidden', 'true')
            // Elimina tabindex y roles
            if (child.hasAttribute('tabindex')) child.removeAttribute('tabindex')
            if (child.hasAttribute('role')) child.removeAttribute('role')
            // NO modificar el texto ni el contenido visual
            console.log('[ARIA-LIVE] hideDefaultPopoverContent: child aria-hidden applied', child.className)
          } else {
            console.log('[ARIA-LIVE] hideDefaultPopoverContent: aria-live-date visible', child.className)
          }
        })
      }

      // Inicializa aria-live con el valor actual
      updateAriaLiveFromCurrentCell()

      // Observa cambios en el td.available.current
      if (!panel.hasAttribute('data-aria-live-observer')) {
        const table = panel.querySelector('.el-date-table')
        if (table) {
          const observer = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
              if (mutation.type === 'attributes' || mutation.type === 'childList') {
                updateAriaLiveFromCurrentCell()
                break
              }
            }
          })
          observer.observe(table, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ['class', 'aria-label', 'tabindex']
          })
          panel.setAttribute('data-aria-live-observer', 'true')
          // Cleanup observer when panel is hidden
          const panelObserver = new MutationObserver(() => {
            const isVisible = panel.offsetParent !== null && panel.style.display !== 'none'
            if (!isVisible) {
              observer.disconnect()
              panel.removeAttribute('data-aria-live-observer')
              panelObserver.disconnect()
              console.log('[ARIA-LIVE] [REMOVED] aria-live MutationObserver')
            }
          })
          panelObserver.observe(panel, { attributes: true, attributeFilter: ['style', 'class'] })
          console.log('[ARIA-LIVE] [REGISTERED] aria-live MutationObserver for td.available.current')
        }
      }
    },

    // Modificado: llama a enhanceDatePickerAccessibility cuando se muestra el popover
    toggleDisplayPopoverDatepicker (className, active = true) {
      const panel = document.querySelector(className)
      console.log('[ARIA-LIVE] toggleDisplayPopoverDatepicker', className, active, panel)
      if (panel) {
        panel.style.display = active ? 'block' : 'none'
        if (active) {
          setTimeout(() => {
            this.enhanceDatePickerAccessibility(className)
          }, 200)
        }
      } else {
        console.warn('[ARIA-LIVE] panel not found for toggleDisplayPopoverDatepicker')
      }
    }, 
    // Modales el-ui
    handleOpenModalDialogMixin (refNames) {
      // console.log('handleOpenModalDialogMixin', refNames)
      // Siempre trabajamos con array
      const refs = Array.isArray(refNames) ? refNames : [refNames]
      this.previouslyFocusedEl = document.activeElement // guarda el foco
      this.$nextTick(() => {
        setTimeout(() => {
          // obtenemos todos los contenedores
          const containers = refs
            .map(name => this.$refs[name])
            .filter(Boolean)
          // buscamos el primer elemento enfocable en todos los contenedores
          let focusable = null
          for (const container of containers) {
            focusable = container.querySelector('input:not([tabindex="-1"]), button:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])')
            if (focusable) break
          }
          if (focusable) focusable.focus()
          this.trapFocusMixin(containers)
        }, 100)
      })
    },
    trapFocusMixin (containers) {
      // console.log('trapFocusMixin', containers)
      this.focusHandler = (e) => {
        if (e.key !== 'Tab') return

        // Excluimos elementos con tabindex="-1" ya que el navegador los salta al tabular
        // (ej: el-input__inner cuando el-select es multiple+filterable)
        const focusableSelector = [
          'a[href]:not(.hidden):not([tabindex="-1"])',
          'area[href]:not([tabindex="-1"])',
          'input:not([disabled]):not([tabindex="-1"])',
          'select:not([disabled]):not([tabindex="-1"])',
          'textarea:not([disabled]):not([tabindex="-1"])',
          'button:not([disabled]):not([tabindex="-1"])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(', ')

        // concatenamos todos los elementos focuseables de todos los contenedores
        // Filtramos por visibilidad para evitar incluir elementos dentro de paneles ocultos
        // (ej: el-tab-pane inactivos con display:none que tienen tabindex="0")
        const focusableEls = containers.flatMap(container =>
          Array.from(container.querySelectorAll(focusableSelector))
            .filter(el => el.offsetParent !== null)
        )
        // console.log('Focusable elements:', focusableEls)
        if (focusableEls.length === 0) return

        const activeEl = document.activeElement
        let idx = focusableEls.indexOf(activeEl)

        // Si el activeEl no está en la lista (ej: el-input__inner con tabindex="-1"),
        // resolvemos la posición efectiva buscando el widget padre más cercano que
        // contenga elementos focusables (ej: el-select contiene el-select__input).
        // Así evitamos navegar hacia atrás/adelante DENTRO del mismo widget.
        if (idx === -1) {
          let widgetBoundary = activeEl.parentElement
          while (widgetBoundary && !containers.some(c => c === widgetBoundary)) {
            if (focusableEls.some(el => widgetBoundary.contains(el))) break
            widgetBoundary = widgetBoundary.parentElement
          }

          if (widgetBoundary && !containers.some(c => c === widgetBoundary)) {
            const widgetIndices = focusableEls
              .map((el, i) => widgetBoundary.contains(el) ? i : -1)
              .filter(i => i !== -1)
            if (widgetIndices.length > 0) {
              // Para Shift+Tab: nos posicionamos en el primer elemento del widget (navegar antes del widget)
              // Para Tab: en el último (navegar después del widget)
              idx = e.shiftKey ? Math.min(...widgetIndices) : Math.max(...widgetIndices)
            }
          }
        }

        // Controlamos la navegación Tab/Shift-Tab nosotros mismos para garantizar
        // un recorrido completo incluyendo elementos detrás del scroll
        e.preventDefault()

        let targetIdx
        if (e.shiftKey) {
          targetIdx = (idx <= 0) ? focusableEls.length - 1 : idx - 1
        } else {
          targetIdx = (idx < 0 || idx >= focusableEls.length - 1) ? 0 : idx + 1
        }

        focusableEls[targetIdx].focus()
      }
      document.addEventListener('keydown', this.focusHandler)
    },
    releaseFocusDialogMixin () {
      document.removeEventListener('keydown', this.focusHandler)
      this.focusHandler = null

      // Restaurar foco al elemento anterior si existe
      if (this.previouslyFocusedEl && typeof this.previouslyFocusedEl.focus === 'function') {
        this.previouslyFocusedEl.focus()
        this.previouslyFocusedEl = null
      }
    },
  },
}

const numberFilter = {
  filters: {
    money: function (value, code = 'EUR', locale = null, format = null) {
      return getCurrencyFormat(value, code, locale, format)
    },
    number: function (value, minDecimal = 0, maxDecimal = 2, locale = null, format = null) {
      return getDecimalFormat(value, minDecimal, maxDecimal, locale, format)
    },
    percent: function (value, minDecimal = 0, maxDecimal = 2, locale = null, format = null) {
      return getPercentFormat(value, minDecimal, maxDecimal, locale, format)
    }
  }
}

export { feedbackMixin, numberFilter }
